import React from 'react';
import { ActionsPanel } from './ActionsPanel';
import { LogPanel } from './LogPanel';
import { useGameStore } from '../../stores/gameStore';
import { useCheatStore } from '../../stores/cheatStore';
import { useCombatStore } from '../../stores/combatStore';
import { combatService } from '../../services/combatService';
import { isPlayerControlled } from '@hexcrawl/shared-utils';
import { COMBAT_ACTION_BAR_HEIGHT } from '../../../combat/constants';
import { CheatGroup } from '../common/CheatGroup';
import { cheatFailureButtonStyle, cheatSuccessButtonStyle } from '../../constants/cheatStyles';

export const CombatComponent: React.FC = () => {
  const gameState = useGameStore((s) => s.gameState);
  const activeCombat = gameState?.activeCombat;
  const cheatsEnabled = useCheatStore((s) => s.cheatsEnabled);
  const queueBusy = useCombatStore((s) => s.queueBusy);
  const combatOutcome = useCombatStore((s) => s.combatOutcome);
  const enqueueResponse = useCombatStore((s) => s.enqueueResponse);

  const handleCheatEnd = async (outcome: 'win' | 'lose') => {
    if (!activeCombat) return;
    try {
      const response = await combatService.cheatEnd(activeCombat.id, outcome);
      if (enqueueResponse) await enqueueResponse(response);
    } catch (err) {
      console.error('Failed to force combat outcome:', err);
    }
  };

  if (!activeCombat) return null;

  const round = activeCombat.round;
  const turn = activeCombat.turnIndex + 1;

  const order = activeCombat.groupOrder && activeCombat.groupOrder.length > 0
    ? activeCombat.groupOrder
    : Array.from(new Set(activeCombat.entityStates.map(c => c.groupId)));

  const fullOrder: string[] = [];
  for (const groupId of order) {
    for (const entityState of activeCombat.entityStates) {
      if (entityState.groupId === groupId) fullOrder.push(entityState.entityId);
    }
  }

  const currentEntityId = fullOrder.length > 0 ? fullOrder[activeCombat.turnIndex % fullOrder.length] : undefined;
  const currentEntityState = currentEntityId ? activeCombat.entityStates.find(e => e.entityId === currentEntityId) : undefined;
  const isPlayerTurn = currentEntityState != null && isPlayerControlled(currentEntityState);
  const actingName = isPlayerTurn ? gameState!.character!.name : (currentEntityId || '—');

  return (
    <div className="combat-ui" style={{
      position: 'fixed',
      inset: 0,
      pointerEvents: 'none',
    }}>
      {/* Log Panel — upper-left overlay, 20% wide, 50% of window height */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0,
        width: '20%',
        height: '50vh',
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'auto',
      }}>
        <LogPanel />
      </div>

      {/* Cheats — instantly win/lose the combat */}
      {cheatsEnabled && !combatOutcome && (
        <div style={{
          position: 'absolute',
          bottom: COMBAT_ACTION_BAR_HEIGHT + 8, right: 8,
          display: 'flex',
          pointerEvents: 'auto',
        }}>
          <CheatGroup>
            <button
              onClick={() => handleCheatEnd('win')}
              disabled={queueBusy}
              title="Cheat: instantly win this combat"
              style={{
                ...cheatSuccessButtonStyle,
                cursor: queueBusy ? 'not-allowed' : 'pointer',
                opacity: queueBusy ? 0.5 : 1,
              }}
            >
              Win
            </button>
            <button
              onClick={() => handleCheatEnd('lose')}
              disabled={queueBusy}
              title="Cheat: instantly lose this combat"
              style={{
                ...cheatFailureButtonStyle,
                cursor: queueBusy ? 'not-allowed' : 'pointer',
                opacity: queueBusy ? 0.5 : 1,
              }}
            >
              Lose
            </button>
          </CheatGroup>
        </div>
      )}

      {/* Round/Turn header — top-center over the map */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        <div style={{
          display: 'flex',
          gap: '24px',
          color: 'white',
          fontSize: '20px',
          textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
          background: 'rgba(26, 20, 34, 0.85)',
          padding: '10px 20px',
          borderRadius: '0 0 12px 12px',
          border: '1px solid #bb86fc',
          borderTop: 'none',
          pointerEvents: 'auto',
        }}>
          <div style={{ color: '#ffffff', fontWeight: 'bold' }}>Round {round}</div>
          <div style={{ color: '#bbbbbb' }}>Turn {turn}</div>
          <div style={{ color: '#88e0ff' }}>Acting: <span style={{ color: isPlayerTurn ? '#a0ffb0' : '#ffb0a0' }}>{actingName}</span></div>
        </div>
      </div>

      {/* Action Panel — full-width bottom bar */}
      <div style={{
        position: 'absolute',
        bottom: 0, left: 0, right: 0,
        minHeight: COMBAT_ACTION_BAR_HEIGHT,
        background: '#0a0a14',
        borderTop: '2px solid #5aa0ff',
        pointerEvents: 'auto',
      }}>
        <ActionsPanel />
      </div>
    </div>
  );
};
